import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import StatusBadge, { STATUS_LIST } from './components/StatusBadge'
import CommentThread from './components/CommentThread'
import { fetchReportDetail, addComment, changeStatus, checkInboxAccess } from './api/issuesApi'
import { getUserId } from '../services/authorize'

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '')

function fmt(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
}

export default function IssueDetail({ mode = 'staff' }) {
  const { ir_id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isDeptHead, setIsDeptHead] = useState(false)
  const [zoomUrl, setZoomUrl] = useState(null)

  const currentId = getUserId()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const d = await fetchReportDetail(mode, ir_id)
      setData(d)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'ไม่สามารถโหลดข้อมูลได้')
    } finally {
      setLoading(false)
    }
  }, [mode, ir_id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (mode === 'staff') {
      checkInboxAccess().then(r => setIsDeptHead(!!r.access)).catch(() => {})
    }
  }, [mode])

  const handleAddComment = async (text) => {
    await addComment(mode, ir_id, text)
    await load()
  }

  const handleChangeStatus = async (status) => {
    const { value: note, isConfirmed } = await Swal.fire({
      title: `เปลี่ยนสถานะเป็น "${STATUS_LIST.find(s => s.value === status)?.label}"`,
      input: 'textarea',
      inputLabel: 'หมายเหตุ (ไม่บังคับ)',
      inputPlaceholder: 'อธิบายเพิ่มเติม...',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (!isConfirmed) return
    try {
      await changeStatus(ir_id, status, note || '')
      await load()
    } catch (err) {
      Swal.fire('ผิดพลาด', err.response?.data?.error || err.message, 'error')
    }
  }

  if (loading) return <div className="text-center py-5"><span className="spinner-border" /></div>
  if (error) return (
    <div className={mode === 'staff' ? 'main main-app p-3 p-lg-4' : 'p-3'}>
      <div className="alert alert-danger">{error}</div>
      <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
        <i className="ri-arrow-left-line me-1" />ย้อนกลับ
      </button>
    </div>
  )
  if (!data) return null

  const isMine =
    (mode === 'staff'   && data.submitter_role === 'staff'   && data.t_id  === currentId) ||
    (mode === 'student' && data.submitter_role === 'student' && data.st_id === currentId)

  const canChangeStatus = mode === 'staff' && isDeptHead
  const status = data.ir_status
  const baseList = '/issues/mine'

  return (
    <div className={mode === 'staff' ? 'main main-app p-3 p-lg-4' : ''} style={mode === 'staff' ? { maxWidth: 900 } : {}}>
      <Link to={baseList} className="btn btn-link p-0 mb-2">
        <i className="ri-arrow-left-line me-1" />กลับ
      </Link>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex align-items-start gap-2 mb-2">
            <i className={`${data.cat_icon || 'ri-tools-line'} fs-3 text-primary`} />
            <div className="flex-grow-1">
              <div className="small text-muted">{data.ir_code}</div>
              <h5 className="mb-1">{data.ir_title}</h5>
              <div className="small text-muted">
                <span className="me-2"><i className="ri-folder-line me-1" />{data.cat_name}</span>
                <span className="me-2"><i className="ri-building-line me-1" />{data.dept_name}</span>
              </div>
            </div>
            <StatusBadge status={status} />
          </div>

          <hr />
          <div className="row g-2 small text-muted mb-2">
            <div className="col-12 col-md-6">
              <i className="ri-user-line me-1" />ผู้แจ้ง: {data.submitter_name || '-'}
              {data.submitter_role === 'student' ? ' (นักเรียน)' : ' (บุคลากร)'}
            </div>
            <div className="col-12 col-md-6">
              <i className="ri-time-line me-1" />แจ้งเมื่อ: {fmt(data.ir_created_at)}
            </div>
            {data.ir_location && (
              <div className="col-12">
                <i className="ri-map-pin-line me-1" />สถานที่: {data.ir_location}
              </div>
            )}
          </div>

          <div className="mb-3" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {data.ir_description}
          </div>

          {data.attachments?.length > 0 && (
            <div className="row g-2">
              {data.attachments.map(a => {
                const url = `${UPLOAD_BASE}/uploads/issues/${a.att_filename}`
                return (
                  <div key={a.att_id} className="col-4 col-sm-3 col-md-2">
                    <img
                      src={url}
                      alt=""
                      className="img-thumbnail w-100"
                      style={{ aspectRatio: '1 / 1', objectFit: 'cover', cursor: 'zoom-in' }}
                      onClick={() => setZoomUrl(url)}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {canChangeStatus && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="fw-semibold mb-2">เปลี่ยนสถานะ (สำหรับหัวหน้าฝ่าย)</div>
            <div className="d-flex flex-wrap gap-2">
              {STATUS_LIST.filter(s => s.value !== status).map(s => (
                <button
                  key={s.value}
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleChangeStatus(s.value)}
                >
                  <i className={`${s.icon} me-1`} />{s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="fw-semibold mb-2"><i className="ri-chat-3-line me-1" />ความคิดเห็น</div>
          <CommentThread
            comments={data.comments || []}
            currentRole={mode === 'student' ? 'student' : 'staff'}
            currentId={currentId}
            onSubmit={handleAddComment}
            disabled={!isMine && !canChangeStatus}
          />
        </div>
      </div>

      {zoomUrl && (
        <div
          onClick={() => setZoomUrl(null)}
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.85)', zIndex: 1080, cursor: 'zoom-out' }}
        >
          <img src={zoomUrl} alt="" style={{ maxWidth: '95%', maxHeight: '95%' }} />
        </div>
      )}
    </div>
  )
}
